import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { Button } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import {
  createEFormSubmission,
  submitEFormSubmission,
  updateEFormSubmission,
} from '../../services/mutations';
import { EFormRuntime } from './EFormRuntime';
import {
  applyEFormRoleAccess,
  flattenSchemaFields,
  isEFormSchema,
  resolveDefaultValue,
  isEFormDisplayField,
  isEFormFileMeta,
  stripHiddenFieldValues,
  validateSubmissionValues,
  validationErrorsByField,
  type SchemaValidationError,
} from './schema';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import {
  useEFormSubmission,
  useEFormAccessContext,
  usePublishedSchema,
} from './useData';

const AUTOSAVE_MS = 1200;

function fillStorageKey(definitionId: number | null, submissionId: number | null): string {
  return `eform-fill:v1:${definitionId ?? 'x'}:${submissionId ?? 'new'}`;
}

/** Persist only JSON-safe non-file/signature values for crash recovery. */
function valuesForLocalStorage(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (isEFormFileMeta(v)) continue;
    if (Array.isArray(v) && v.some(isEFormFileMeta)) continue;
    if (v && typeof v === 'object' && !Array.isArray(v) && isEFormFileMeta(v)) continue;
    // Signature file meta or stamp objects
    if (
      v &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      'storageKey' in (v as object)
    ) {
      continue;
    }
    try {
      JSON.stringify(v);
      out[k] = v;
    } catch {
      /* skip non-serializable */
    }
  }
  return out;
}

function readLocalRecovery(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { values?: Record<string, unknown>; savedAt?: number };
    if (!parsed?.values || typeof parsed.values !== 'object') return null;
    return parsed.values;
  } catch {
    return null;
  }
}

function writeLocalRecovery(key: string, values: Record<string, unknown>) {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ values: valuesForLocalStorage(values), savedAt: Date.now() }),
    );
  } catch {
    /* quota / private mode */
  }
}

function clearLocalRecovery(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function scrollToFirstError(fieldKey?: string) {
  if (!fieldKey) return;
  const el = document.querySelector(`[data-field-key="${CSS.escape(fieldKey)}"]`);
  if (el && 'scrollIntoView' in el) {
    (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

const FillPage: React.FC = () => {
  const { definitionId: defParam, id: submissionParam } = useParams<{
    definitionId?: string;
    id?: string;
  }>();
  const definitionId = defParam ? parseInt(defParam, 10) : NaN;
  const submissionId = submissionParam ? parseInt(submissionParam, 10) : NaN;
  const isEditSubmission = Number.isInteger(submissionId);

  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { setTitleOverride } = useTopbar();
  const { showSuccess, showError } = useNotification();

  const published = usePublishedSchema(
    isEditSubmission ? null : Number.isInteger(definitionId) ? definitionId : null,
  );
  const existing = useEFormSubmission(isEditSubmission ? submissionId : 0);

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [activeSubmissionId, setActiveSubmissionId] = useState<number | null>(
    isEditSubmission ? submissionId : null,
  );
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'unsaved' | 'saving' | 'saved'>('idle');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [errorSummary, setErrorSummary] = useState<SchemaValidationError[]>([]);
  const seededRef = useRef(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveInFlight = useRef(false);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const subformValidators = useRef<Record<string, (() => boolean) | null>>({});

  const resolvedDefinitionId = useMemo(() => {
    if (Number.isInteger(definitionId)) return definitionId;
    if (existing.data?.definitionId) return existing.data.definitionId;
    if (published.data?.definition.id) return published.data.definition.id;
    return null;
  }, [definitionId, existing.data?.definitionId, published.data?.definition.id]);
  const accessContext = useEFormAccessContext(resolvedDefinitionId);

  const schema = useMemo(() => {
    const publishedSchema =
      published.data?.schemaJson && isEFormSchema(published.data.schemaJson)
        ? published.data.schemaJson
        : null;
    const snapshot =
      isEditSubmission && existing.data?.schemaSnapshot && isEFormSchema(existing.data.schemaSnapshot)
        ? existing.data.schemaSnapshot
        : null;
    const base = snapshot ?? publishedSchema;
    if (!base) return null;
    if (snapshot && publishedSchema?.settings) {
      return applyEFormRoleAccess({
        ...snapshot,
        settings: {
          ...snapshot.settings,
          requireApproval: publishedSchema.settings.requireApproval,
          approvalTemplateCode: publishedSchema.settings.approvalTemplateCode,
        },
      }, accessContext.data?.role);
    }
    return applyEFormRoleAccess(base, accessContext.data?.role);
  }, [isEditSubmission, existing.data, published.data, accessContext.data?.role]);

  const title = isEditSubmission
    ? existing.data?.definition?.name ?? 'Submission'
    : published.data?.definition.name ?? 'Fill form';

  const readOnly =
    (isEditSubmission && existing.data?.status === 'SUBMITTED') ||
    Boolean(existing.data?.locked) ||
    accessContext.data?.canFill === false;

  const recoveryKey = fillStorageKey(resolvedDefinitionId, activeSubmissionId);

  const userDisplayName =
    user?.userDisplayName ||
    [user?.userFirstName, user?.userLastName].filter(Boolean).join(' ') ||
    user?.userEmail ||
    '';

  useEffect(() => {
    setTitleOverride(title);
    return () => setTitleOverride(undefined);
  }, [title, setTitleOverride]);

  useEffect(() => {
    if (seededRef.current) return;
    if (isEditSubmission && existing.data?.valuesJson) {
      const defId = existing.data.definitionId ?? resolvedDefinitionId;
      const key = fillStorageKey(defId, existing.data.id);
      const server = { ...(existing.data.valuesJson as Record<string, unknown>) };
      const recovered = !readOnly ? readLocalRecovery(key) : null;
      setValues(recovered ? { ...server, ...recovered } : server);
      if (recovered) setSaveStatus('unsaved');
      seededRef.current = true;
    } else if (!isEditSubmission && published.data && schema) {
      const defId = published.data.definition.id;
      const key = fillStorageKey(defId, null);
      const initial: Record<string, unknown> = {};
      for (const field of flattenSchemaFields(schema)) {
        if (isEFormDisplayField(field)) continue;
        if (field.type === 'subform') {
          initial[field.key] = [];
          continue;
        }
        const def = resolveDefaultValue(field, { userDisplayName });
        if (def !== undefined) initial[field.key] = def;
      }
      const recovered = readLocalRecovery(key);
      setValues(recovered ? { ...initial, ...recovered } : initial);
      if (recovered) setSaveStatus('unsaved');
      seededRef.current = true;
    }
  }, [
    isEditSubmission,
    existing.data,
    published.data,
    schema,
    userDisplayName,
    resolvedDefinitionId,
    readOnly,
  ]);

  // Keep calculated fields in sync
  useEffect(() => {
    if (!schema || readOnly) return;
    const fields = flattenSchemaFields(schema).filter((f) => f.calculatedFrom);
    if (!fields.length) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const f of fields) {
        const src = f.calculatedFrom!;
        if (next[f.key] !== prev[src]) {
          next[f.key] = prev[src];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [schema, values, readOnly]);

  const isDirty = saveStatus === 'unsaved' || saveStatus === 'saving';
  const { confirmLeave } = useUnsavedChangesGuard(isDirty && !readOnly);

  const registerSubformValidate = useCallback(
    (fieldKey: string, validate: (() => boolean) | null) => {
      subformValidators.current[fieldKey] = validate;
    },
    [],
  );

  const persistValues = useCallback(
    async (nextValues: Record<string, unknown>) => {
      if (!schema) throw new Error('Missing schema');
      const cleaned = stripHiddenFieldValues(schema, nextValues);
      let id = activeSubmissionId;
      if (id) {
        await updateEFormSubmission(id, cleaned);
      } else {
        const defId = resolvedDefinitionId;
        if (!defId) throw new Error('Missing form');
        const created = await createEFormSubmission({ definitionId: defId, values: cleaned });
        id = created.id;
        setActiveSubmissionId(id);
        clearLocalRecovery(fillStorageKey(defId, null));
        navigate(`/e-forms/submissions/${id}/edit`, { replace: true });
      }
      clearLocalRecovery(fillStorageKey(resolvedDefinitionId, id));
      clearLocalRecovery(recoveryKey);
      await existing.mutate?.();
      return id;
    },
    [
      schema,
      activeSubmissionId,
      resolvedDefinitionId,
      navigate,
      existing,
      recoveryKey,
    ],
  );

  const scheduleAutosave = useCallback(() => {
    if (readOnly || !schema) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      if (autosaveInFlight.current || readOnly) return;
      autosaveInFlight.current = true;
      setSaveStatus('saving');
      try {
        await persistValues(valuesRef.current);
        setSaveStatus('saved');
      } catch (e: any) {
        setSaveStatus('unsaved');
        console.warn('E-form autosave failed', e?.message || e);
      } finally {
        autosaveInFlight.current = false;
      }
    }, AUTOSAVE_MS);
  }, [readOnly, schema, persistValues]);

  useEffect(() => {
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, []);

  const setFieldValue = useCallback(
    (name: string, value: unknown) => {
      setValues((prev) => {
        const next = { ...prev, [name]: value };
        writeLocalRecovery(
          fillStorageKey(resolvedDefinitionId, activeSubmissionId),
          next,
        );
        return next;
      });
      setFieldErrors((prev) => {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
      setErrorSummary([]);
      setSaveStatus('unsaved');
      scheduleAutosave();
    },
    [resolvedDefinitionId, activeSubmissionId, scheduleAutosave],
  );

  const handleSaveDraft = async () => {
    if (!schema || readOnly) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setSaving(true);
    setSaveStatus('saving');
    try {
      const id = await persistValues(values);
      setSaveStatus('saved');
      showSuccess(id ? 'Draft saved' : 'Draft created');
    } catch (e: any) {
      setSaveStatus('unsaved');
      showError(e?.response?.data?.message || e?.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!schema || readOnly) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

    // Inline subform cell validation (optional hooks)
    let subformOk = true;
    for (const fn of Object.values(subformValidators.current)) {
      if (fn && !fn()) subformOk = false;
    }

    const cleaned = stripHiddenFieldValues(schema, values);
    const errors = validateSubmissionValues(schema, cleaned, { requireRequired: true });
    if (errors.length || !subformOk) {
      setErrorSummary(errors);
      setFieldErrors(validationErrorsByField(errors));
      scrollToFirstError(errors[0]?.fieldKey);
      showError(errors[0]?.message || 'Please fix the highlighted fields');
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setErrorSummary([]);
    try {
      const id = await persistValues(cleaned);
      await submitEFormSubmission(id!);
      clearLocalRecovery(fillStorageKey(resolvedDefinitionId, id));
      setSaveStatus('saved');
      showSuccess(
        schema?.settings?.requireApproval
          ? 'Form submitted — approval request created'
          : 'Form submitted',
      );
      navigate(`/e-forms/submissions/${id}`);
    } catch (e: any) {
      const apiErrors = Array.isArray(e?.response?.data?.errors)
        ? (e.response.data.errors as SchemaValidationError[])
        : null;
      if (apiErrors?.length) {
        setErrorSummary(apiErrors);
        setFieldErrors(validationErrorsByField(apiErrors));
        scrollToFirstError(apiErrors[0]?.fieldKey);
      }
      const msg =
        e?.response?.data?.message ||
        (apiErrors ? apiErrors.map((x) => x.message).join('; ') : null) ||
        e?.message ||
        'Failed to submit';
      showError(typeof msg === 'string' ? msg : 'Failed to submit');
      setSaveStatus('unsaved');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirmLeave())) return;
    navigate(-1);
  };

  const loading = isEditSubmission ? existing.isLoading : published.isLoading;
  if (loading || !schema) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="large" message="Loading form…" />
      </div>
    );
  }

  if (isEditSubmission && existing.isError) {
    return (
      <div className={`p-6 ${colors.text}`}>
        <p>Submission not found.</p>
        <Link
          to={
            Number.isInteger(definitionId)
              ? `/e-forms/${definitionId}/submissions`
              : existing.data?.definitionId
                ? `/e-forms/${existing.data.definitionId}/submissions`
                : '/e-forms'
          }
          className="text-blue-600"
        >
          Back to submissions
        </Link>
      </div>
    );
  }

  const saveLabel =
    saveStatus === 'saving'
      ? 'Saving…'
      : saveStatus === 'saved'
        ? 'Saved'
        : saveStatus === 'unsaved'
          ? 'Unsaved'
          : null;

  return (
    <div className={cn('w-full pb-8 -mx-4 px-2 sm:px-3', colors.text)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          {existing.data?.status ? (
            <p className={`text-sm ${colors.textMuted}`}>
              Status: {existing.data.status}
              {existing.data.locked ? ' · Locked' : ''}
            </p>
          ) : null}
          {!readOnly && saveLabel ? (
            <p
              className={cn(
                'text-xs',
                existing.data?.status ? 'mt-0.5' : '',
                saveStatus === 'unsaved' ? 'text-amber-600' : colors.textMuted,
              )}
            >
              {saveLabel}
            </p>
          ) : null}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={() => void handleCancel()}>
            Cancel
          </Button>
          {!readOnly ? (
            <>
              <Button variant="secondary" onClick={() => void handleSaveDraft()} disabled={saving || submitting}>
                {saving ? 'Saving…' : 'Save draft'}
              </Button>
              <Button variant="default" onClick={() => void handleSubmit()} disabled={saving || submitting}>
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </>
          ) : (
            <Link to={`/e-forms/submissions/${submissionId}`}>
              <Button variant="secondary">View detail</Button>
            </Link>
          )}
        </div>
      </div>

      {errorSummary.length > 0 ? (
        <div
          className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          <p className="font-medium mb-1">Please fix the following:</p>
          <ul className="list-disc pl-5 space-y-0.5">
            {errorSummary.slice(0, 12).map((e, i) => (
              <li key={`${e.fieldKey ?? 'x'}-${i}`}>
                {e.fieldKey ? (
                  <button
                    type="button"
                    className="underline text-left"
                    onClick={() => scrollToFirstError(e.fieldKey)}
                  >
                    {e.message}
                  </button>
                ) : (
                  e.message
                )}
              </li>
            ))}
            {errorSummary.length > 12 ? (
              <li>…and {errorSummary.length - 12} more</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {schema.instructions ? (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm whitespace-pre-wrap ${colors.border} ${colors.surface} ${colors.textSecondary}`}
        >
          {schema.instructions}
        </div>
      ) : null}

      <div className={`w-full min-w-0 rounded-lg border ${colors.border} ${colors.surface} p-3 sm:p-4`}>
        <EFormRuntime
          schema={schema}
          values={values}
          onChange={setFieldValue}
          readOnly={readOnly}
          errors={fieldErrors}
          variant="compact"
          onRegisterSubformValidate={registerSubformValidate}
        />
      </div>
    </div>
  );
};

export default FillPage;
